import React from 'react';
import { motion } from 'motion/react';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { SpotlightHover } from '../ui/spotlight-hover';
import { appScreenshots, mascot } from '../../data/mock';
import { useTranslation } from 'react-i18next';
import { STORE_LINKS } from '../../constants/links';

const CTASection = () => {
  const { t } = useTranslation();

  return (
    <section className="py-24 lg:py-36 bg-white text-slate-900 relative overflow-hidden z-10 border-t border-slate-100">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative bg-[#FAF9F6] rounded-[3rem] p-8 sm:p-12 lg:p-20 overflow-hidden border border-slate-200"
        >
          {/* Spotlight Hover */}
          <SpotlightHover className="from-slate-500/5 to-transparent" size={400} />

          <div className="relative grid lg:grid-cols-2 gap-12 items-center z-10">
            {/* Left Content */}
            <motion.div
              className="text-center lg:text-left"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4.5 py-1.5 mb-6 shadow-sm"
              >
                <motion.div
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles className="w-4 h-4 text-slate-800" />
                </motion.div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-800">{t('cta.badge')}</span>
              </motion.div>

              <h2 className="text-4xl sm:text-5xl font-black text-slate-950 mb-6 leading-tight tracking-tighter">
                {t('cta.title1')} <br />
                {t('cta.title2')}
              </h2>

              <p className="text-slate-500 text-sm mb-10 max-w-md mx-auto lg:mx-0 leading-relaxed font-semibold">
                {t('cta.description')}
              </p>

              {/* Download Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <motion.a
                  href={STORE_LINKS.ios}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    size="lg"
                    className="bg-slate-900 hover:bg-slate-800 text-white font-black rounded-full px-8 py-6 text-xs uppercase tracking-wider w-full sm:w-auto shadow-sm group border-none"
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                    App Store
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </motion.a>
                <motion.a
                  href={STORE_LINKS.android}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    size="lg"
                    variant="outline"
                    className="border border-slate-350 text-slate-800 hover:bg-slate-50 rounded-full px-8 py-6 text-xs uppercase tracking-wider w-full sm:w-auto group font-semibold shadow-sm"
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                    Google Play
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </motion.a>
              </div>
            </motion.div>

            {/* Right Content: Phone Mockup with floating notifications */}
            <motion.div
              initial={{ opacity: 0, x: 40, rotateY: 8 }}
              whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:flex justify-center relative z-10"
            >
              <div className="relative group/mock">
                
                {/* Large Mascot overlay */}
                <motion.div
                  className="absolute -top-16 -right-10 z-30 w-32 h-32"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.8, type: "spring", stiffness: 150, damping: 15 }}
                >
                  <motion.img
                    src={mascot.main}
                    alt="Monito viajero"
                    className="w-full h-full object-contain"
                    animate={{
                      y: [0, -6, 0],
                      rotate: [0, 3, -3, 0]
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                </motion.div>

                {/* Silver Phone Mockup */}
                <motion.div
                  className="w-60 sm:w-64"
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="bg-slate-100 rounded-[3rem] p-2 shadow-xl border border-slate-200">
                    <div className="bg-white rounded-[2.3rem] overflow-hidden border border-slate-100">
                      <img
                        src={appScreenshots.mapCTA}
                        alt="APEKAI Places App screen"
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Floating Notification Card 1 */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 0.95 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1, type: "spring", stiffness: 150, damping: 14 }}
                  className="absolute -left-12 top-1/3 bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-3"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-900 animate-pulse" />
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-800">{t('cta.floating1')}</span>
                  </div>
                </motion.div>

                {/* Floating Notification Card 2 */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 0.95 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1.2, type: "spring", stiffness: 150, damping: 14 }}
                  className="absolute -right-6 bottom-1/4 bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-3"
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center border border-slate-200">
                      <Sparkles className="w-4 h-4 text-slate-800" />
                    </div>
                    <div className="text-left">
                      <div className="text-[8px] text-slate-400 uppercase font-extrabold tracking-wider">{t('cta.floating2Title')}</div>
                      <div className="text-[10px] font-black uppercase tracking-wider text-slate-800">{t('cta.floating2Subtitle')}</div>
                    </div>
                  </div>
                </motion.div>

              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
